import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IAuthResponse,
  IPublicBusinessConfig,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  SharedBusinessService,
  SubjectManageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import { LOCAL_STORAGE_KEYS } from '@g99-product-web-ui/shared-util-lib';
import { take } from 'rxjs';

@Component({
  selector: 'g99-redirect-from-old-ui',
  templateUrl: './redirect-from-old-ui.component.html',
  styleUrls: ['./redirect-from-old-ui.component.scss'],
  standalone: false,
})
export class RedirectFromOldUiComponent implements OnInit {
  // UI States
  public isClimbing = false;
  public currentMessage = 'Logging in...';
  public isLoading = false;

  // Global variable to store the current user
  protected currentUser: Partial<IAuthResponse> = {};
  protected tokenExpiration = '';

  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _router: Router,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _toastService: ToastService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _sharedService: SharedBusinessService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this._resetAppState(); // Clear previous data from localStorage and subjects
    this._activatedRoute.queryParams.subscribe((params) => {
      // Extract parameters from URL
      const idToken = params['token'];
      const businessId = params['businessId'];
      const firstName = params['firstName'];
      const id = params['id'];
      const lastName = params['lastName'];
      const roles = params['roles'] ? JSON.parse(params['roles']) : null;

      // Handle missing or 'null' expiration
      let tokenExpiration = params['expires'];
      if (tokenExpiration === 'null' || !tokenExpiration) {
        // Default to 1 hour from now if null
        tokenExpiration = (Date.now() + 3600 * 1000).toString();
      }

      // Prepare the current user object and store it in global variable
      this.currentUser = {
        idToken,
        businessId,
        firstName,
        id,
        lastName,
        roles,
      };
      this.tokenExpiration = tokenExpiration;

      // Store businessId immediately to ensure X-TenantID header is available for subsequent calls
      if (businessId) {
        this._localStorageService.storeItem(LOCAL_STORAGE_KEYS.BUSINESS_INFO, {
          id: businessId,
        });
      }

      // If businessId exists, fetch business data
      if (this.currentUser?.businessId) {
        this._localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.CURRENT_USER,
          this.currentUser
        );
        this._localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION,
          this.tokenExpiration
        );
        this._fetchBusinessData(this.currentUser?.businessId);
      }
    });
  }

  /**
   * Handles storing user and business data in localStorage and updating relevant subjects.
   * This method centralizes the localStorage and subject updates for both user and business data.
   *
   * @param {any} businessData - The business data associated with the user.
   */
  private _handleLocalStorageAndSubjects(
    businessData: IPublicBusinessConfig
  ): void {
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.BUSINESS_INFO,
      businessData
    );
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION,
      this.tokenExpiration
    );
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.CURRENT_USER,
      this.currentUser
    );

    // Update subjects
    this._subjectManagerService.businessSubject.next(businessData);
    this._subjectManagerService.userSubject.next(
      this.currentUser as IAuthResponse
    );
    this._startClimbing(); // Show progress message
  }

  /**
   * Fetches the business data associated with the logged-in user and updates the application state.
   * Calls _handleLocalStorageAndSubjects to store the retrieved data and update subjects.
   * Redirects the user to the business page on success.
   *
   * @param {any} bid - The business id
   */
  private _fetchBusinessData(bid: string): void {
    this._sharedService
      .fetchBusinessProfile(+bid)
      .pipe(take(1)) // Take only the first emitted value
      .subscribe({
        next: (businessData) => {
          // Call _handleLocalStorageAndSubjects to store both user and business data
          this._handleLocalStorageAndSubjects(businessData);
        },
        error: (error: Error) => {
          // On error, stop loading and show error message
          this.isLoading = false;
          this._toastService.showError(error.message);
        },
      });
  }

  /**
   * Resets the application state by clearing local storage and resetting user and business subjects.
   */
  private _resetAppState(): void {
    // Clear all data from localStorage
    this._localStorageService.clearStorage();
    // Reset the user and business subjects
    this._subjectManagerService.userSubject.next(null);
    this._subjectManagerService.businessSubject.next(null);
  }

  /**
   * Creates a dynamic and interactive user experience during the login process.
   * Displays progress messages while loading.
   */
  private _startClimbing(): void {
    this.isClimbing = true;
    setTimeout(() => {
      // Update the message after 3 seconds
      this.currentMessage = 'You are almost there!';
      this._router.navigate(['/business']);
      this.isLoading = false;
    }, 2000);
  }
}
